const LEGACY_SW_RELOAD_KEY = 'legacy-service-worker-reload-v1';
const LEGACY_CACHE_MARKERS = [
  'gatsby-plugin-offline',
  'offline-plugin',
  'workbox',
];

const hasReloadMarker = (storage) => {
  try {
    return storage?.getItem(LEGACY_SW_RELOAD_KEY) === '1';
  } catch {
    return false;
  }
};

const setReloadMarker = (storage) => {
  try {
    storage?.setItem(LEGACY_SW_RELOAD_KEY, '1');
  } catch {
    // Ignore storage access failures in private/incognito contexts.
  }
};

const clearReloadMarker = (storage) => {
  try {
    storage?.removeItem(LEGACY_SW_RELOAD_KEY);
  } catch {
    // Ignore storage access failures in private/incognito contexts.
  }
};

const isLegacyServiceWorkerCache = (cacheName = '') =>
  LEGACY_CACHE_MARKERS.some(
    (marker) => cacheName.includes(marker) || cacheName.startsWith(marker),
  );

const unregisterServiceWorkers = async (serviceWorker) => {
  if (!serviceWorker?.getRegistrations) {
    return { hadRegistrations: false, removedRegistrations: false };
  }

  try {
    const registrations = await serviceWorker.getRegistrations();

    if (!registrations.length) {
      return { hadRegistrations: false, removedRegistrations: false };
    }

    const results = await Promise.all(
      registrations.map(async (registration) => {
        try {
          return await registration.unregister();
        } catch {
          return false;
        }
      }),
    );

    return {
      hadRegistrations: true,
      removedRegistrations: results.some(Boolean),
    };
  } catch {
    return { hadRegistrations: false, removedRegistrations: false };
  }
};

const clearLegacyCaches = async (cacheStorage) => {
  if (!cacheStorage?.keys || !cacheStorage?.delete) {
    return { hadLegacyCaches: false, removedLegacyCaches: false };
  }

  try {
    const cacheNames = await cacheStorage.keys();
    const legacyCacheNames = cacheNames.filter(isLegacyServiceWorkerCache);

    if (!legacyCacheNames.length) {
      return { hadLegacyCaches: false, removedLegacyCaches: false };
    }

    const results = await Promise.all(
      legacyCacheNames.map(async (cacheName) => {
        try {
          return await cacheStorage.delete(cacheName);
        } catch {
          return false;
        }
      }),
    );

    return {
      hadLegacyCaches: true,
      removedLegacyCaches: results.some(Boolean),
    };
  } catch {
    return { hadLegacyCaches: false, removedLegacyCaches: false };
  }
};

const cleanupLegacyServiceWorkers = async ({
  browserNavigator = typeof navigator !== 'undefined' ? navigator : undefined,
  cacheStorage = typeof caches !== 'undefined' ? caches : undefined,
  sessionStorage = typeof window !== 'undefined'
    ? window.sessionStorage
    : undefined,
  location = typeof window !== 'undefined' ? window.location : undefined,
} = {}) => {
  const serviceWorker = browserNavigator?.serviceWorker;

  if (!serviceWorker) {
    clearReloadMarker(sessionStorage);
    return false;
  }

  const [{ hadRegistrations, removedRegistrations }, {
    hadLegacyCaches,
    removedLegacyCaches,
  }] = await Promise.all([
    unregisterServiceWorkers(serviceWorker),
    clearLegacyCaches(cacheStorage),
  ]);

  const shouldReload = Boolean(serviceWorker.controller)
    || hadRegistrations
    || removedRegistrations
    || hadLegacyCaches
    || removedLegacyCaches;

  if (!shouldReload) {
    clearReloadMarker(sessionStorage);
    return false;
  }

  if (hasReloadMarker(sessionStorage)) {
    return true;
  }

  setReloadMarker(sessionStorage);
  location?.reload?.();

  return true;
};

module.exports = {
  LEGACY_SW_RELOAD_KEY,
  cleanupLegacyServiceWorkers,
  isLegacyServiceWorkerCache,
};
